import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeGrid } from 'react-window';
import styled from 'styled-components';

import { deleteImage } from 'apis/mips/api';
import { PhotoShootImageData } from 'apis/mips/types';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { getErrorContent } from 'common/utils';

import ImagesGalleryCellRenderer from './ImagesGalleryCellRenderer';
import { Breakpoints } from '../../constants/breakpoints';
import { PhotoShootQueryKeys } from '../../constants/queryKeys';
import usePhotoShootContext from '../../hooks/usePhotoShootContext';
import { PhotoShootItem } from '../../types';
import { combineRefs } from '../../utils/combineRefs';

export type ImagesGalleryProps = {
  images: PhotoShootImageData[];
  photoShootItems: PhotoShootItem[];
  isReadOnly?: boolean;
};

const Root = styled.div`
  height: 100%;
`;

const getColumnCount = () => {
  const isBigDesktop = window.innerWidth >= Breakpoints.DESKTOP && window.innerWidth < Breakpoints.BIG_DESKTOP;
  const isDesktop = window.innerWidth >= Breakpoints.MOBILE && window.innerWidth < Breakpoints.DESKTOP;
  const isMobile = window.innerWidth < Breakpoints.MOBILE;

  if (isBigDesktop) {
    return 6;
  }

  if (isDesktop) {
    return 4;
  }

  if (isMobile) {
    return 3;
  }

  return 8;
};

export const ImagesGallery = forwardRef<VariableSizeGrid | null, ImagesGalleryProps>(
  ({ images, photoShootItems, isReadOnly = false }: ImagesGalleryProps, ref) => {
    const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
    const [columnCount, setColumCount] = useState(getColumnCount());
    const autoSizerSize = useRef({ height: 0, width: 0, padding: 0 });
    const localGridRef = useRef<VariableSizeGrid | null>(null);
    const queryClient = useQueryClient();
    const { venueId, token } = usePhotoShootContext();
    const isMobile = useIsMobile();
    const rowCount = Math.ceil(images.length / columnCount);
    const AUTO_SIZER_PADDING = isMobile ? 8 : 32;

    const { mutate: handleDelete } = useMutation((imageId: string) => deleteImage(venueId, imageId, token), {
      onSuccess: res => queryClient.setQueryData(PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token), res.data),
      onError: (error: unknown) => toast.error(getErrorContent(error) || ''),
      onMutate: imageId =>
        setLoadingImages(prevState => {
          const newState = new Set(prevState);
          newState.add(imageId);
          return newState;
        }),
      onSettled: (_, __, imageId) =>
        setLoadingImages(prevState => {
          const newState = new Set(prevState);
          newState.delete(imageId);
          return newState;
        }),
    });

    const imagesToRender = useMemo(
      () =>
        images.map(image => ({
          ...image,
          columnCount,
          rowCount,
          onDelete: handleDelete,
          isDeleting: loadingImages.has(image.id),
          isReadOnly,
          isLinked: photoShootItems.some(
            item => item.images && item.images.some(itemImage => itemImage.id === image.id),
          ),
        })),
      [images, columnCount, rowCount, handleDelete, loadingImages, isReadOnly, photoShootItems],
    );

    useLayoutEffect(() => {
      const handleResize = () => setColumCount(getColumnCount());

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
      <Root>
        <AutoSizer style={{ width: '100%', height: '100%', padding: `0px ${AUTO_SIZER_PADDING}px` }}>
          {({ height, width }) => {
            // 9 / 16 ratio
            const columnWidth = (width - 2 * AUTO_SIZER_PADDING) / columnCount;
            const rowHeight = columnWidth * 0.5625;
            if (
              autoSizerSize.current.height !== height ||
              autoSizerSize.current.width !== width ||
              autoSizerSize.current.padding !== AUTO_SIZER_PADDING
            ) {
              localGridRef.current?.resetAfterIndices({ columnIndex: 0, rowIndex: 0, shouldForceUpdate: false });
              autoSizerSize.current = { height, width, padding: AUTO_SIZER_PADDING };
            }

            return (
              <VariableSizeGrid
                ref={element => combineRefs(element, [ref, localGridRef])}
                overscanColumnCount={0}
                width={width - AUTO_SIZER_PADDING}
                height={height}
                columnWidth={() => columnWidth}
                rowHeight={() => rowHeight}
                columnCount={columnCount}
                rowCount={rowCount}
                itemData={imagesToRender}
                overscanRowCount={5}
              >
                {ImagesGalleryCellRenderer}
              </VariableSizeGrid>
            );
          }}
        </AutoSizer>
      </Root>
    );
  },
);
